import React from 'react';
import { Link } from 'react-router-dom';
import Card from './Card';
import { motion } from 'framer-motion';
import { fadeIn, staggerContainer, scaleUp } from './utils/animations';
import AnimateWhenVisible from './components/AnimateWhenVisible';

function Pricing() {
  const pricingPlans = [
    {
      id: 1,
      name: "Landing Page",
      price: 150,
      description: "Perfect for businesses needing a high-converting 1-page landing page to establish your online presence.",
      features: [
        { text: "1-Page Landing Page", subtext: "Fully responsive design" },
        { text: "Conversion-Focused CTAs", subtext: "Optimized for user journey" },
        { text: "2 Weeks Support", subtext: "For changes and updates" },
        { text: "1 Year Hosting Included", subtext: "Worry-free web presence" }
      ],
      popular: false
    },
    {
      id: 2,
      name: "Standard Website",
      price: 599,
      description: "Complete website solution with hosting included to establish your brand online and attract more customers.",
      features: [
        { text: "5-Page Custom Website", subtext: "Fully responsive design" },
        { text: "Custom Contact Forms", subtext: "For leads and customer engagement" },
        { text: "Simple CMS System", subtext: "Easy content updates" },
        { text: "1 Year Hosting Included", subtext: "Secure and reliable" }
      ],
      popular: true
    },
    {
      id: 3,
      name: "Premium Website",
      price: 1999,
      description: "Fully customized website solution tailored to your exact business needs with comprehensive back-end functionality.",
      features: [
        { text: "Unlimited Custom Pages", subtext: "Fully responsive design" },
        { text: "Complete Back-End Setup", subtext: "Authentication & user management" },
        { text: "Custom CMS Integration", subtext: "Easy content management" },
        { text: "1 Year Maintenance", subtext: "Priority support & updates" }
      ],
      popular: false
    }
  ];
  
  return (
    <motion.div 
      variants={staggerContainer(0.1, 0.2)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.25 }}
      className="container mx-auto px-4 py-20"
    >
      <motion.div 
        variants={fadeIn('up', 0.2)}
        className="relative mb-16 text-center"
      >
        <div className="absolute left-1/2 top-1/2 -z-10 h-36 w-36 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/0 blur-2xl" />
        <h1 className="relative text-4xl font-bold text-white md:text-5xl lg:text-6xl">
          Website <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">Solutions</span>
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-400">
          Boost your brand's online presence with custom websites that attract and convert customers.
        </p>
      </motion.div>
      
      <div className="flex flex-wrap items-center justify-center gap-8">
        {pricingPlans.map((plan, index) => (
          <AnimateWhenVisible 
            key={plan.id} 
            variants={scaleUp}
            threshold={0.2}
            className="group relative w-80"
          >
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-slate-950 to-slate-900 p-[1px] shadow-2xl transition-all duration-300 hover:-translate-y-2 hover:shadow-cyan-500/25">
              <div className="absolute inset-0 bg-gradient-to-b from-cyan-500 to-blue-500 opacity-20" />
              <div className="relative rounded-2xl bg-gradient-to-b from-slate-950 to-slate-900 p-6">
                <div className="absolute -left-16 -top-16 h-32 w-32 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/0 blur-2xl transition-all duration-500 group-hover:scale-150 group-hover:opacity-70" />
                <div className="absolute -bottom-16 -right-16 h-32 w-32 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/0 blur-2xl transition-all duration-500 group-hover:scale-150 group-hover:opacity-70" />
                
                {plan.popular && (
                  <div className="absolute -right-[1px] -top-[1px] overflow-hidden rounded-tr-2xl">
                    <div className="absolute h-20 w-20 bg-gradient-to-r from-cyan-500 to-blue-500" />
                    <div className="absolute h-20 w-20 bg-slate-950/90" />
                    <div className="absolute right-0 top-[22px] h-[2px] w-[56px] rotate-45 bg-gradient-to-r from-cyan-500 to-blue-500" />
                    <span className="absolute right-1 top-1 text-[10px] font-semibold text-white">POPULAR</span>
                  </div>
                )}
                
                <div className="relative">
                  <h3 className="text-sm font-medium uppercase tracking-wider text-cyan-500">
                    {plan.name}
                  </h3>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-white">${plan.price}</span>
                    <span className="text-sm text-slate-400">one-time</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-400">
                    {plan.description}
                  </p>
                </div>
                
                <div className="relative mt-6 space-y-4">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cyan-500/10">
                        <svg stroke="currentColor" viewBox="0 0 24 24" fill="none" className="h-4 w-4 text-cyan-500">
                          <path d="M5 13l4 4L19 7" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{feature.text}</p>
                        <p className="text-xs text-slate-400">{feature.subtext}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="relative mt-8">
                  <Link to="/contact" className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 p-px font-semibold text-white shadow-[0_1000px_0_0_hsl(0_0%_100%_/_0%)_inset] transition-colors hover:shadow-[0_1000px_0_0_hsl(0_0%_100%_/_2%)_inset] w-full block">
                    <div className="relative rounded-xl bg-slate-950/50 px-4 py-2 transition-colors group-hover:bg-transparent">
                      <span className="relative flex items-center justify-center gap-2">
                        Get Started
                        <svg stroke="currentColor" viewBox="0 0 24 24" fill="none" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1">
                          <path d="M17 8l4 4m0 0l-4 4m4-4H3" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                        </svg>
                      </span>
                    </div>
                  </Link>
                </div>
                
                <div className="mt-4 flex items-center justify-center gap-2">
                  <svg stroke="currentColor" viewBox="0 0 24 24" fill="none" className="h-4 w-4 text-slate-400">
                    <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                  </svg>
                  <span className="text-xs font-medium text-slate-400">Custom quotes available</span>
                </div>
              </div>
            </div>
          </AnimateWhenVisible>
        ))}
      </div>
      
      <motion.div 
        variants={fadeIn('up', 0.4)}
        className="mt-20 text-center"
      >
        <div className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-800/50 px-4 py-2">
          <svg stroke="currentColor" viewBox="0 0 24 24" fill="none" className="h-5 w-5 text-cyan-500">
            <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          </svg>
          <span className="text-sm font-medium text-white">All projects include a satisfaction guarantee</span>
        </div>
        
        <div className="mt-8">
          <Link to="/contact" className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 p-px font-semibold text-white shadow-[0_1000px_0_0_hsl(0_0%_100%_/_0%)_inset] transition-colors hover:shadow-[0_1000px_0_0_hsl(0_0%_100%_/_2%)_inset] inline-block">
            <div className="relative rounded-xl bg-slate-950/50 px-4 py-2 transition-colors group-hover:bg-transparent">
              <span className="relative flex items-center justify-center gap-2">
                Contact for Custom Quote
                <svg stroke="currentColor" viewBox="0 0 24 24" fill="none" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1">
                  <path d="M17 8l4 4m0 0l-4 4m4-4H3" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                </svg>
              </span>
            </div>
          </Link>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default Pricing;
